import { yupResolver } from "@hookform/resolvers/yup";
import { createJobDriveSlotApi, getJobListApi, updateJobDriveSlotApi } from "api/services/jobs";
import { Button } from "components/Button";
import DateInput from "components/Form/dateInput";
import EssFullWidthModal from "components/modelpopup/baseModal";
import { format, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { Bounce, toast } from "react-toastify";
import { customStyleSelect } from "utils/common";
import * as Yup from "yup";

const ScheduleSlotModal = ({
  open,
  onCancel,
  jobId = null, // jobId from parent (filter selection)
  slot,
  onSuccess,
  loading
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const slotSchema = Yup.object().shape({
    job: Yup.object().required("Job is required"),
    title: Yup.string().required("Title is required"),
    date: Yup.string().required("Date is required"),
    start_time: Yup.string().required("Start time is required"),
    end_time: Yup.string().required("End time is required"),
    capacity: Yup.number().min(1, "Must be at least 1").required("Capacity is required"),
    location: Yup.string(),
    notes: Yup.string(),
    status: Yup.string().required("Status is required"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(slotSchema),
    defaultValues: {
      job: null,
      title: "",
      date: "",
      start_time: "",
      end_time: "",
      capacity: 50,
      location: "",
      notes: "",
      status: "active",
    },
  });

  // Fetch jobs when modal opens
  useEffect(() => {
    if (open) {
      fetchJobs();
    }
  }, [open]);

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const response = await getJobListApi({ job_drive_enabled: true });
      setJobs(response.data?.results || []);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setJobsLoading(false);
    }
  };

  // Prepare job options - memoized to prevent infinite loops
  const jobOptions = useMemo(() => {
    return jobs?.map(job => ({
      label: job.title,
      value: job.id
    })) || [];
  }, [jobs]);

  // Set form values when slot changes (for editing) - set all fields except job
  useEffect(() => {
    if (!open || !slot) return;
    
    // Parse ISO datetime strings to date and time
    if (slot.start_at) {
      const startDate = parseISO(slot.start_at);
      setValue("date", startDate);
      setValue("start_time", format(startDate, "HH:mm"));
    }
    if (slot.end_at) {
      const endDate = parseISO(slot.end_at);
      setValue("end_time", format(endDate, "HH:mm"));
    }
    setValue("title", slot.title || "");
    setValue("capacity", slot.capacity || 50);
    setValue("location", slot.location || "");
    setValue("notes", slot.notes || "");
    setValue("status", slot.status || "active");
  }, [slot, open, setValue]);

  // Set job field separately - wait for jobs to load
  useEffect(() => {
    if (!open || !jobOptions.length) return;
    
    if (slot) {
      // Set job if slot has a job (handle both object and ID)
      if (slot.job) {
        const jobId = typeof slot.job === 'object' ? slot.job.id : slot.job;
        const jobOption = jobOptions.find(opt => opt.value === jobId);
        if (jobOption) {
          setValue("job", jobOption);
          setSelectedJob(jobOption);
        }
      }
    } else {
      // If jobId is provided from parent (filter), pre-select it
      if (jobId) {
        const jobOption = jobOptions.find(opt => opt.value === jobId);
        if (jobOption) {
          setValue("job", jobOption);
          setSelectedJob(jobOption);
        }
      } else {
        setSelectedJob(null);
      }
    }
  }, [slot, open, jobOptions, jobId, setValue]);

  // Reset form when modal closes or when creating new slot
  useEffect(() => {
    if (!open) {
      reset();
      setSelectedJob(null);
    } else if (open && !slot) {
      reset();
    }
  }, [open, slot, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Combine date and time into ISO datetime strings
      const dateStr = format(new Date(data.date), "yyyy-MM-dd");
      const startDateTime = `${dateStr}T${data.start_time}:00Z`;
      const endDateTime = `${dateStr}T${data.end_time}:00Z`;

      const formattedData = {
        job: data.job.value,
        title: data.title,
        start_at: startDateTime,
        end_at: endDateTime,
        capacity: data.capacity,
        location: data.location || null,
        notes: data.notes || null,
        status: data.status,
      };

      if (slot?.id) {
        await updateJobDriveSlotApi(slot.id, formattedData);
        toast.success("Time slot updated successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } else {
        await createJobDriveSlotApi(formattedData);
        toast.success("Time slot created successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
      reset();
      if (onSuccess) onSuccess();
      onCancel();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || "Failed to save time slot", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onCancel();
  };

  return (
    <EssFullWidthModal
      mask={false}
      title={slot?.id ? "Edit Time Slot" : "Create Time Slot"}
      open={open}
      width="40%"
      footer={[]}
      onCancel={handleClose}
      className="full-width-modal"
    >
      <div className="modal-content">
        <div className="modal-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Job <span className="text-danger">*</span></label>
                  <Controller
                    name="job"
                    control={control}
                    render={({ field }) => (
                      <Select
                        placeholder="Select a job"
                        value={field.value}
                        onChange={(option) => {
                          field.onChange(option);
                          setSelectedJob(option);
                        }}
                        options={jobOptions}
                        styles={customStyleSelect}
                        isDisabled={isLoading || loading || jobsLoading}
                        isLoading={jobsLoading}
                      />
                    )}
                  />
                  {errors.job && (
                    <small className="text-danger">{errors.job?.message}</small>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Title <span className="text-danger">*</span></label>
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <input
                        className="form-control"
                        type="text"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="e.g., Morning Batch, Afternoon Session"
                      />
                    )}
                  />
                  {errors.title && (
                    <small className="text-danger">{errors.title?.message}</small>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Date <span className="text-danger">*</span></label>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field }) => (
                      <DateInput
                        className="form-control"
                        selected={field.value}
                        onChange={field.onChange}
                        dateFormat="dd-MM-yyyy"
                        minDate={new Date()}
                      />
                    )}
                  />
                  {errors.date && (
                    <small className="text-danger">{errors.date?.message}</small>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Start Time <span className="text-danger">*</span></label>
                  <Controller
                    name="start_time"
                    control={control}
                    render={({ field }) => (
                      <input
                        className="form-control"
                        type="time"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {errors.start_time && (
                    <small className="text-danger">{errors.start_time?.message}</small>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">End Time <span className="text-danger">*</span></label>
                  <Controller
                    name="end_time"
                    control={control}
                    render={({ field }) => (
                      <input
                        className="form-control"
                        type="time"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {errors.end_time && (
                    <small className="text-danger">{errors.end_time?.message}</small>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Capacity <span className="text-danger">*</span></label>
                  <Controller
                    name="capacity"
                    control={control}
                    render={({ field }) => (
                      <input
                        className="form-control"
                        type="number"
                        min="1"
                        value={field.value}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                        placeholder="Maximum number of candidates"
                      />
                    )}
                  />
                  {errors.capacity && (
                    <small className="text-danger">{errors.capacity?.message}</small>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Status <span className="text-danger">*</span></label>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <select
                        className="form-control"
                        value={field.value}
                        onChange={field.onChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    )}
                  />
                  {errors.status && (
                    <small className="text-danger">{errors.status?.message}</small>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Location</label>
                  <Controller
                    name="location"
                    control={control}
                    render={({ field }) => (
                      <input
                        className="form-control"
                        type="text"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="e.g., Store #12, Main Office"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Notes</label>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <textarea
                        className="form-control"
                        rows={3}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Additional instructions or notes"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="submit-section">
              <Button
                loading={isLoading || loading}
                disabled={isLoading || loading}
                className="btn btn-primary submit-btn"
                type="submit"
                label={slot?.id ? "Update Slot" : "Create Slot"}
              />
            </div>
          </form>
        </div>
      </div>
    </EssFullWidthModal>
  );
};

export default ScheduleSlotModal;

